<template>
  <div
    v-if="health.mode === 'building'"
    ref="rootRef"
    class="relative inline-flex"
  >
    <button
      ref="triggerRef"
      type="button"
      class="inline-flex h-10 items-center justify-center bg-indigo-600 px-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:bg-indigo-500 dark:hover:bg-indigo-400 dark:focus:ring-offset-slate-950"
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-haspopup="dialog"
      @click="toggle"
    >
      🚢 I shipped
    </button>

    <form
      v-if="isOpen"
      class="absolute right-0 top-full z-30 mt-2 w-72 border border-slate-200 bg-white p-3 text-sm shadow-lg dark:border-slate-800 dark:bg-slate-900 dark:shadow-black/30"
      @submit.prevent="submit"
      @keydown.escape.prevent.stop="close"
    >
      <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300" for="ship-label">
        Label <span class="font-normal text-slate-400 dark:text-slate-500">optional</span>
      </label>
      <input
        id="ship-label"
        ref="labelInputRef"
        v-model="label"
        type="text"
        class="mt-1 block h-9 w-full border border-slate-300 bg-white px-2.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
        placeholder="v1, update 2…"
        :disabled="submitting"
      >

      <label class="mt-3 block text-xs font-semibold text-slate-700 dark:text-slate-300" for="ship-url">
        URL <span class="font-normal text-slate-400 dark:text-slate-500">optional</span>
      </label>
      <input
        id="ship-url"
        v-model="url"
        type="url"
        class="mt-1 block h-9 w-full border border-slate-300 bg-white px-2.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
        placeholder="https://…"
        :disabled="submitting"
      >

      <ul
        v-if="errors.length"
        class="mt-3 space-y-1 text-xs text-red-700 dark:text-red-300"
        role="alert"
      >
        <li v-for="error in errors" :key="error">{{ error }}</li>
      </ul>

      <button
        type="submit"
        class="mt-3 inline-flex h-9 w-full items-center justify-center bg-slate-950 px-3 text-sm font-semibold text-white hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-600 dark:hover:bg-indigo-500"
      :disabled="submitting"
    >
        Ship it
      </button>
    </form>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useApi } from '../../composables/useApi'

const props = defineProps({
  health: {
    type: Object,
    required: true,
  },
  workspaceSlug: {
    type: String,
    required: true,
  },
})

const emit = defineEmits(['shipped'])
const api = useApi()
const rootRef = ref(null)
const triggerRef = ref(null)
const labelInputRef = ref(null)
const isOpen = ref(false)
const label = ref('')
const url = ref('')
const submitting = ref(false)
const errors = ref([])

watch(
  () => props.health.mode,
  (mode) => {
    if (mode !== 'building') close({ restoreFocus: false })
  }
)

onMounted(() => {
  document.addEventListener('pointerdown', handleOutsidePointer)
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleOutsidePointer)
})

defineExpose({
  open,
})

function toggle() {
  isOpen.value ? close() : open()
}

function open() {
  if (props.health.mode !== 'building') return

  errors.value = []
  isOpen.value = true
  nextTick(() => labelInputRef.value?.focus())
}

function close({ restoreFocus = true } = {}) {
  if (!isOpen.value) return

  isOpen.value = false
  if (restoreFocus) {
    nextTick(() => triggerRef.value?.focus())
  }
}

function handleOutsidePointer(event) {
  if (!isOpen.value || rootRef.value?.contains(event.target)) return
  close({ restoreFocus: false })
}

async function submit() {
  if (submitting.value) return

  submitting.value = true
  errors.value = []

  try {
    const payload = await api.shipWorkspace(props.workspaceSlug, {
      label: label.value.trim(),
      url: url.value.trim(),
    })
    label.value = ''
    url.value = ''
    emit('shipped', payload)
    close()
  } catch (error) {
    errors.value = Array.isArray(error?.data?.errors)
      ? error.data.errors
      : [error?.message || 'Could not record ship.']
  } finally {
    submitting.value = false
  }
}
</script>
